import {
  countCardsInColumn,
  countCardsInRow,
  countCardsInSwimlane,
  emptyStructuredContext,
  ownContextLayer,
  parentContextLayers,
  setScopeContext,
  structuredContextSchema,
  type StructuredContext,
} from '@blueprint/domain'
import { useEffect, useMemo, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button } from '../../components/controls.js'
import { useBoardEditable, useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { ContextEditor } from './ContextEditor.js'
import styles from './context.module.css'

const SCOPE_NAMES = { board: 'Board', swimlane: 'Swimlane', row: 'Row', column: 'Column' } as const

/**
 * Edits the architecture context of the board, a swimlane, a row or a column. Saving
 * writes only this scope; every card inside it picks the change up on its next read.
 */
export function ScopeContextDialog() {
  const owner = useUiStore((state) => state.contextOwner)
  const setOwner = useUiStore((state) => state.setContextOwner)
  const entities = useBoardStore((state) => state.entities)
  const editable = useBoardEditable()
  const [draft, setDraft] = useState<StructuredContext>(emptyStructuredContext())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const own = useMemo(() => (entities && owner ? ownContextLayer(entities, owner) : null), [entities, owner])
  const parents = useMemo(() => (entities && owner ? parentContextLayers(entities, owner) : []), [entities, owner])

  useEffect(() => {
    if (!owner) return
    setDraft(structuredContextSchema.parse(own?.context ?? {}))
    setErrors({})
    // Re-seed when a different owner opens, not when the board changes underneath.
  }, [owner?.scope, owner && 'id' in owner ? owner.id : null])

  if (!owner || !entities || !own) return null

  const cardCount =
    owner.scope === 'board'
      ? Object.keys(entities.cards).length
      : owner.scope === 'column'
        ? countCardsInColumn(entities, owner.id)
        : owner.scope === 'row'
          ? countCardsInRow(entities, owner.id)
          : countCardsInSwimlane(entities, owner.id)

  const close = () => setOwner(null)
  const save = () => {
    const parsed = structuredContextSchema.safeParse(draft)
    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) next[`context.${issue.path.join('.')}`] ??= issue.message
      setErrors(next)
      return
    }
    useBoardStore.getState().run((state) => setScopeContext(state, owner, parsed.data))
    close()
  }

  return (
    <Dialog
      open
      xwide
      title={`${SCOPE_NAMES[owner.scope]} context: ${own.source.title}`}
      onClose={close}
      onSubmit={editable ? save : undefined}
      footer={
        <>
          <Button onClick={close}>{editable ? 'Cancel' : 'Close'}</Button>
          {editable ? (
            <Button variant="primary" type="submit">
              Save
            </Button>
          ) : null}
        </>
      }
    >
      <div className={styles.stack}>
        <p className={styles.hint}>
          Software context written here is inherited by {cardCount} card{cardCount === 1 ? '' : 's'}
          {owner.scope === 'board' ? ' on this board' : ` in this ${owner.scope}`} and used in their build prompts.
          Cards keep their own overrides and exclusions; nothing is copied onto them.
        </p>
        <ContextEditor value={draft} onChange={setDraft} parents={parents} errors={errors} />
      </div>
    </Dialog>
  )
}
